import { TextField } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText/FormHelperText";
import { useMutation } from "@tanstack/react-query";
import { FC } from "react";
import { useParams } from "react-router-dom";
import { Address } from "ton";
import { JettonMinter, JettonMinterContent } from "ton-wrappers";
import { ProposalActions } from "../../libs/daoService";
import { getTonClient } from "../../libs/tonClient";
import { seeIfValidAddress } from "../NewProposal";
import {
  CustomMessageFields,
  CustomMessageParams,
  RequiredError,
  validateCustomMessageParams,
} from "./NoneActionFields";

export interface JettonData {
  isDeployed: boolean;
  jettonWalletAddress: Address;
  jettonContent: Partial<JettonMinterContent> | null;
}

export interface SendJettonActionProps extends CustomMessageParams {
  kind: ProposalActions.sendJetton;
  address: string;
  jettonMinterAddress: string;
  jettonAmount: string;
  jettonData?: JettonData;
}

export const validateSendJettonActionParams = async (
  action: Partial<SendJettonActionProps>
): Promise<Partial<SendJettonActionProps>> => {
  const value: Partial<SendJettonActionProps> = { kind: action.kind };

  if (!seeIfValidAddress(action.address)) {
    Object.assign(value, { address: "Invalid Address" });
  }

  if (!seeIfValidAddress(action.jettonMinterAddress)) {
    Object.assign(value, { jettonMinterAddress: "Invalid Address" });
  }

  const { jettonData } = action;
  if (!jettonData || !jettonData.jettonContent) {
    Object.assign(value, { jettonMinterAddress: "Failed to load Jetton data" });
  } else if (!jettonData.isDeployed) {
    Object.assign(value, {
      jettonMinterAddress: "Jetton wallet is not deployed",
    });
  } else if (!("decimals" in jettonData.jettonContent)) {
    Object.assign(value, {
      jettonMinterAddress: "Failed to load jetton decimals",
    });
  }

  if (!action.jettonAmount) {
    Object.assign(value, { jettonAmount: RequiredError });
  }

  Object.assign(value, validateCustomMessageParams(action));

  return value;
};

const useJettonData = () => {
  const { address } = useParams();

  return useMutation<JettonData, Error, string>(async (jettonMinterAddress) => {
    const tonClient = await getTonClient();
    const master = tonClient.open(
      JettonMinter.createFromAddress(Address.parse(jettonMinterAddress))
    );

    const jettonWalletAddress = await master.getWalletAddress(
      Address.parse(address!)
    );

    const isDeployed = await tonClient.isContractDeployed(jettonWalletAddress);
    const jettonData = await master.getJettonData();

    return {
      isDeployed,
      jettonWalletAddress,
      jettonContent: jettonData.jettonContent,
    };
  });
};

export const SendJettonAction: FC<{
  params: Partial<SendJettonActionProps>;
  setParams: (value: Partial<SendJettonActionProps>) => void;
  disabled: boolean;
  submitted: boolean;
  validation: Partial<SendJettonActionProps> | undefined;
}> = ({ params, setParams, disabled, submitted, validation }) => {
  const { mutateAsync, isLoading, reset } = useJettonData();
  const onJettonMinterAddress = async (jettonMinterAddress: string) => {
    setParams({ jettonMinterAddress });
    reset();
    const jettonData = await mutateAsync(jettonMinterAddress);
    setParams({ jettonData });
  };

  return (
    <>
      <div>
        <FormHelperText sx={{ mx: "12px" }}>Wallet Address</FormHelperText>
        <TextField
          fullWidth
          value={params.address}
          disabled={disabled}
          onChange={(e) => setParams({ address: e.target.value })}
          error={submitted && validation?.address != null}
          helperText={submitted && validation?.address}
        />
      </div>

      <div>
        <FormHelperText sx={{ mx: "12px" }}>
          Jetton Minter Address
        </FormHelperText>
        <TextField
          fullWidth
          value={params.jettonMinterAddress}
          disabled={disabled || isLoading}
          onChange={(e) => onJettonMinterAddress(e.target.value)}
          error={submitted && validation?.jettonMinterAddress != null}
          helperText={submitted && validation?.jettonMinterAddress}
        />
      </div>

      <div>
        <FormHelperText sx={{ mx: "12px" }}>Amount</FormHelperText>
        <TextField
          fullWidth
          type="number"
          value={params.jettonAmount}
          disabled={disabled}
          onChange={(e) => setParams({ jettonAmount: e.target.value })}
          error={submitted && validation?.jettonAmount != null}
          helperText={submitted && validation?.jettonAmount}
        />
      </div>

      <CustomMessageFields
        params={params}
        setParams={setParams}
        disabled={disabled}
        submitted={submitted}
        validation={validation}
      />
    </>
  );
};
